import type { CurrentUser } from '@btsis/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { forgetEveryNameExceptMe } from './name-visibility';

const ME = ['auth', 'me'] as const;
const EMAIL_CHANGE = ['auth', 'profile', 'emailChange'] as const;

/** A change to your own account answers with who you now are, which replaces the signed-in user. */
function useSelfMutation<Input, Output extends { user: CurrentUser }>(
  run: (input: Input) => Promise<Output>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (result) => queryClient.setQueryData(ME, result.user),
  });
}

export function useUpdateProfile() {
  return useSelfMutation((input: { name: string }) => api.auth.profile.update(input));
}

/** The address change waiting for its link, which the profile shows until it is followed. */
export function usePendingEmailChange() {
  return useQuery({
    queryKey: EMAIL_CHANGE,
    queryFn: async () => (await api.auth.profile.emailChange({})).pending,
  });
}

/** Asks for the link; the address itself does not change, so the signed-in user stays as it is. */
export function useChangeEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; currentPassword: string }) =>
      api.auth.profile.changeEmail(input),
    onSuccess: (result) => queryClient.setQueryData(EMAIL_CHANGE, result.pending),
  });
}

export function useConfirmEmailChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string }) => api.auth.profile.confirmEmailChange(input),
    onSuccess: (result) => {
      queryClient.setQueryData(ME, result.user);
      queryClient.setQueryData(EMAIL_CHANGE, null);
    },
  });
}

export function useChangePassword() {
  return useSelfMutation((input: { currentPassword: string; newPassword: string }) =>
    api.auth.profile.changePassword(input),
  );
}

/** Replacing a second factor: the first step changes nothing the user has, the second replaces it and answers with who they now are. */
export function useStartTwoFactorReenrolment() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; code: string }) =>
      api.auth.twoFactor.reenrol.start(input),
  });
}

export function useConfirmTwoFactorReenrolment() {
  return useSelfMutation((input: { enrolmentToken: string; secret: string; code: string }) =>
    api.auth.twoFactor.reenrol.confirm(input),
  );
}

export function useDisableTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { password: string }) => api.auth.twoFactor.disable(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ME }),
  });
}

/**
 * Opening or closing real names changes what every cached answer says, so every
 * query but the signed-in user is reset — inactive ones included, which a refetch
 * would leave holding the other state's names — the server decides the names,
 * never the browser (docs/decisions/0136).
 */
function useNameVisibilityMutation<Input>(run: (input: Input) => Promise<{ user: CurrentUser }>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (result) => forgetEveryNameExceptMe(queryClient, result.user),
  });
}

export function useRevealPseudonyms() {
  return useNameVisibilityMutation((input: { code: string }) => api.auth.pseudonyms.reveal(input));
}

export function useHidePseudonyms() {
  return useNameVisibilityMutation((_input: undefined) => api.auth.pseudonyms.hide({}));
}
